/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import "./KatexEquationAlterer.css";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import * as React from "react";
import { useCallback, useState } from "react";

import KatexRenderer from "./KatexRenderer";
import { useTranslation } from "react-i18next";
import TextInput from "./TextInput";
import styled from "styled-components";
import { DialogActions } from "./Dialog";
import { Button } from "@slid/slid-ips";

type Props = {
  initialEquation?: string;
  onConfirm: (equation: string, inline: boolean) => void;
};

export default function KatexEquationAlterer({ onConfirm, initialEquation = "" }: Props): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [equation, setEquation] = useState<string>(initialEquation);
  const [inline, setInline] = useState<boolean>(false);
  const { t } = useTranslation("EditorModal");

  const onClick = useCallback(() => {
    onConfirm(equation, inline);
  }, [onConfirm, equation, inline]);

  return (
    <>
      {equation && (
        <KatexRendererWrapper>
          <KatexRenderer equation={equation} inline={false} onDoubleClick={() => null} />
        </KatexRendererWrapper>
      )}
      <TextInput
        onChange={(event) => {
          setEquation(event);
        }}
        value={equation}
        placeholder={t("equation.placeholder", { keySeparator: ".", ns: "EditorModal" })}
      />
      <DialogActions>
        <Button appearance="primary" size="medium" isDisabled={!equation} callback={onClick} text={t("equation.createButton", { keySeparator: ".", ns: "EditorModal" })} />
      </DialogActions>
    </>
  );
}

const KatexRendererWrapper = styled.div`
  display: flex;
  height: 80px;
  width: 360px;
  padding: 16px;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;

  border-radius: 8px;
  border: 1px solid var(--blue6);
  background: var(--white);
  margin-bottom: 8px;
  overflow-x: auto;
`;
